
import { useRef, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { getReverseGeocode } from "@/utils/mapUtils";
import type { Location } from "@/components/maps/types";

interface UseMapEventsProps {
  map: google.maps.Map | null;
  marker: google.maps.marker.AdvancedMarkerElement | null;
  onLocationSelected: (location: Location) => void;
}

export const useMapEvents = ({ map, marker, onLocationSelected }: UseMapEventsProps) => {
  const mapClickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const markerDragListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!map || !marker) return;

    // Clean up any existing listeners
    if (mapClickListenerRef.current) {
      google.maps.event.removeListener(mapClickListenerRef.current);
    }
    if (markerDragListenerRef.current) {
      google.maps.event.removeListener(markerDragListenerRef.current);
    }

    const handleMapClick = (event: google.maps.MapMouseEvent) => {
      try {
        const clickedLocation = event.latLng;
        if (clickedLocation && marker) {
          console.log("Map clicked at:", clickedLocation.toString());
          
          marker.position = clickedLocation;
          
          try {
            map.panTo(clickedLocation);
          } catch (panError) {
            console.warn("panTo not available, falling back to setCenter:", panError);
            map.setCenter(clickedLocation);
          }
          
          if (clickedLocation) {
            getReverseGeocode(
              clickedLocation.lat(), 
              clickedLocation.lng(), 
              onLocationSelected,
              true
            );
          }
        }
      } catch (error) {
        console.error("Error handling map click:", error);
        toast({
          title: "Map Error",
          description: "There was a problem processing your map click. Please try again.",
          variant: "destructive",
        });
      }
    };

    const handleMarkerDrag = () => {
      try {
        const position = marker.position;
        if (position) {
          console.log("Marker dragged to:", position.toString ? position.toString() : JSON.stringify(position));
          
          // Extract lat/lng values safely based on the position type
          let lat: number = 0;
          let lng: number = 0;
          
          // Handle position based on its type
          if (position instanceof google.maps.LatLng) {
            // For LatLng objects
            lat = position.lat();
            lng = position.lng();
            
            try {
              map.panTo(position);
            } catch (panError) {
              console.warn("panTo not available, falling back to setCenter:", panError);
              map.setCenter(position);
            }
          } else {
            // For LatLngLiteral or other position formats
            // Safely handle lat property
            if (typeof position === 'object' && 'lat' in position) {
              if (typeof position.lat === 'number') {
                lat = position.lat;
              } else {
                // Log the issue for debugging
                console.log("Position lat type:", typeof position.lat);
                // Fallback value
                lat = 0;
              }
            }
            
            // Safely handle lng property
            if (typeof position === 'object' && 'lng' in position) {
              if (typeof position.lng === 'number') {
                lng = position.lng;
              } else {
                // Log the issue for debugging
                console.log("Position lng type:", typeof position.lng);
                // Fallback value
                lng = 0;
              }
            }
            
            // Create a proper LatLng object for map methods
            const latLngPosition = new google.maps.LatLng(lat, lng);
            
            try {
              map.panTo(latLngPosition);
            } catch (panError) {
              console.warn("panTo not available, falling back to setCenter:", panError);
              map.setCenter(latLngPosition);
            }
          }
          
          // Now we have lat and lng as numbers, we can call the geocode function
          getReverseGeocode(
            lat, 
            lng, 
            onLocationSelected,
            true
          );
        }
      } catch (error) {
        console.error("Error handling marker drag:", error);
        toast({
          title: "Map Error",
          description: "There was a problem processing your marker drag. Please try again.",
          variant: "destructive",
        });
      }
    };

    mapClickListenerRef.current = map.addListener("click", handleMapClick);
    marker.addListener("dragend", handleMarkerDrag);

    return () => {
      if (mapClickListenerRef.current) {
        google.maps.event.removeListener(mapClickListenerRef.current);
      }
      if (markerDragListenerRef.current) {
        google.maps.event.removeListener(markerDragListenerRef.current);
      }
    };
  }, [map, marker, onLocationSelected, toast]);
};
